import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, Settings, LogOut, User, Globe, Bell, X, ArrowLeft } from 'lucide-react';
import { io } from 'socket.io-client';
import apiService from '../config';
import UserProfileModal from '../Pages/UserProfile';
import { useLanguage } from '../StoreContext/LanguageContext';
import toast from 'react-hot-toast';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'https://iisdigital.cloud';

// ============================================
// useNotifications Hook
// ============================================
const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);

  const getUserId = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        return user?.data?.usr_id || user?.usr_id;
      }
    } catch { return null; }
  };

  const fetchNotifications = async () => {
    try {
      const res = await apiService.getNotification();
      if (res.status === 200) {
        const data = res.data || [];
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.is_read).length);
      }
    } catch (err) {
      console.error('❌ Notification fetch error:', err);
    }
  };

  // markAsRead returns updated notif so caller can use it
  const markAsRead = async (notificationId) => {
    try {
      // console.log("Marking ID:", notificationId);
      await apiService.patchUpdateSingleNotification(notificationId);

      setNotifications(prev => {
        const updated = prev.map(n =>
          Number(n.notification_id) === Number(notificationId)
            ? { ...n, is_read: 1 }
            : n
        );

        setUnreadCount(updated.filter(n => !n.is_read).length);

        return updated;
      });

    } catch (err) {
      console.error('❌ Mark read error:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiService.patchUpdateAllNotification();

      setNotifications(prev => {
        const updated = prev.map(n => ({
          ...n,
          is_read: true
        }));

        setUnreadCount(0);

        return updated;
      });

    } catch (err) {
      console.error('❌ Mark all read error:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const userId = getUserId();
    if (!userId) return;

    const socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 500,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      // console.log('🔌 Socket connected:', socket.id);
      socket.emit('join', String(userId));
    });

    socket.on('reconnect', () => {
      socket.emit('join', String(userId));
    });

    socket.on('new_notification', (newNotif) => {
      setNotifications(prev => [newNotif, ...prev]);
      setUnreadCount(prev => prev + 1);

      try {
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.6;
        audio.play();
      } catch (e) { }

      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(newNotif.title || 'BhuSampadan', {
          body: newNotif.message,
          icon: '/logo192.png',
        });
      }

      toast.custom((t) => (
        <div className={`max-w-sm w-full bg-white shadow-2xl rounded-2xl flex items-start gap-3 p-4 border-l-4 border-green-500 ${t.visible ? 'opacity-100' : 'opacity-0'}`}
          style={{ transition: 'all 0.3s ease' }}>
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{newNotif.title}</p>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-line">{newNotif.message}</p>
            <p className="text-[10px] text-gray-400 mt-1">
              {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <button onClick={() => toast.dismiss(t.id)} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      ), { duration: 5000, position: 'top-right' });
    });

    socket.on('connect_error', (err) => console.error('❌ Socket error:', err.message));
    socket.on('disconnect', (reason) => console.log('❌ Socket disconnected:', reason));

    return () => { socket.disconnect(); };
  }, []);

  return { notifications, unreadCount, markAsRead, markAllAsRead };
};

// ============================================
// useUserProfile
// ============================================
const useUserProfile = () => {
  const [userInfo, setUserInfo] = useState({ name: 'User', role: 'User', email: 'user@example.com' });
  useEffect(() => {
    const fetch_ = async () => {
      try {
        const result = await apiService.getProfile();
        if (result.status === 200 && result.data) {
          const d = result.data.data || result.data;
          setUserInfo({ name: d.full_name || 'User', role: d.user_role_name || d.user_role || 'User', email: d.email || 'user@example.com' });
          localStorage.setItem('userData', JSON.stringify(result.data));
        }
      } catch {
        try {
          const s = localStorage.getItem('userData');
          if (s) { const d = (JSON.parse(s)).data || JSON.parse(s); setUserInfo({ name: d.full_name || 'User', role: d.user_role_name || 'User', email: d.email || 'user@example.com' }); }
        } catch { }
      }
    };
    fetch_();
  }, []);
  return userInfo;
};

// ============================================
// useCurrentProject
// ============================================
const useCurrentProject = () => {
  const [currentProject, setCurrentProject] = useState(() => { try { return JSON.parse(localStorage.getItem('selectedProject')) || null; } catch { return null; } });
  useEffect(() => {
    const load = () => { try { const p = localStorage.getItem('selectedProject'); if (p) setCurrentProject(JSON.parse(p)); } catch { } };
    load();
    window.addEventListener('storage', (e) => e.key === 'selectedProject' && load());
    const interval = setInterval(load, 2000);
    return () => clearInterval(interval);
  }, []);
  return currentProject;
};

// ============================================
// useCurrentProposal
// ============================================
const useCurrentProposal = () => {
  const [currentProposal, setCurrentProposal] = useState(() => { try { return JSON.parse(localStorage.getItem('selectedProposal')) || null; } catch { return null; } });
  const [lastFetched, setLastFetched] = useState({ projectId: null, proposalId: null, projectCategory: null });

  const getIds = () => {
    try {
      const sp = localStorage.getItem('selectedProject');
      const project = sp ? JSON.parse(sp) : null;
      const projectId = project?.id || project?.project_id;
      const category = project?.project_category || project?.category || 'General';
      const cpId = localStorage.getItem('currentProposalId');
      const ud = localStorage.getItem('userData');
      const d = ud ? (JSON.parse(ud).data || JSON.parse(ud)) : null;
      const proposalId = cpId ? parseInt(cpId) : (d?.default_proposal ? parseInt(d.default_proposal) : null);
      return { projectId, proposalId, category };
    } catch { return { projectId: null, proposalId: null, category: 'General' }; }
  };

  localStorage.setItem("proposalDate", currentProposal?.acquisition_proposal_date);

  useEffect(() => {
    const load = async () => {
      const { projectId, proposalId, category } = getIds();
      if (!projectId) { setCurrentProposal(null); return; }
      if (lastFetched.projectId === projectId && lastFetched.proposalId === proposalId && lastFetched.projectCategory === category) return;
      try {
        let url = `/getAllUserProposal?projectId=${projectId}`;
        if (category === 'Railway') url = `/getDashboardRailwayProposal?projectId=${projectId}`;
        if (category === 'Highway') url = `/getDashboardHighwayProposal?projectId=${projectId}`;
        const res = await apiService.get(url);
        if (res.status === 200 && Array.isArray(res.data) && res.data.length > 0) {
          const found = proposalId ? res.data.find(p => p.acquisition_proposal_id === proposalId) : null;
          const proposal = found || res.data[0];
          localStorage.setItem('selectedProposal', JSON.stringify(proposal));
          localStorage.setItem('currentProposalId', String(proposal.acquisition_proposal_id));
          setCurrentProposal(proposal);
          setLastFetched({ projectId, proposalId, projectCategory: category });
        }
      } catch { }
    };
    load();
    const interval = setInterval(() => {
      const { projectId, proposalId, category } = getIds();
      if (projectId !== lastFetched.projectId || proposalId !== lastFetched.proposalId || category !== lastFetched.projectCategory) load();
    }, 2000);
    window.addEventListener('storage', load);
    window.addEventListener('projectCategoryChanged', load);
    return () => { clearInterval(interval); window.removeEventListener('storage', load); window.removeEventListener('projectCategoryChanged', load); };
  }, [lastFetched]);

  return currentProposal;
};

// ============================================
// useExtendDateData
// ============================================
const useExtendDateData = () => {
  const [extendData, setExtendData] = useState(null);
  const [loading, setLoading] = useState(false);
  const currentProject = useCurrentProject();
  const currentProposal = useCurrentProposal();
  const [lastIds, setLastIds] = useState({ projectId: null, proposalId: null });
  useEffect(() => {
    const fetch_ = async () => {
      const projectId = currentProject?.id || currentProject?.project_id;
      const proposalId = currentProposal?.acquisition_proposal_id;
      if (!projectId || !proposalId || (lastIds.projectId === projectId && lastIds.proposalId === proposalId)) return;
      setLoading(true);
      try {
        const r1 = await apiService.getExtendDate(projectId, proposalId, 'section11');
        const r2 = await apiService.getExtendDate(projectId, proposalId, 'section19');
        setExtendData(r2?.data || r1?.data || null);
        setLastIds({ projectId, proposalId });
      } catch { setExtendData(null); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [currentProject?.id, currentProject?.project_id, currentProposal?.acquisition_proposal_id]);
  return { extendData, loading };
};

// ============================================
// NotificationPanel — List + Detail View
// ============================================
const NotificationPanel = ({ notifications, unreadCount, markAsRead, markAllAsRead, onClose }) => {

  const [selectedNotif, setSelectedNotif] = useState(null);

  useEffect(() => {
    if (selectedNotif) {
      const updated = notifications.find(n => n.notification_id === selectedNotif.notification_id);
      console.log("updated notification", updated);

      if (updated) {
        setSelectedNotif(updated);
      }
    }
  }, [notifications]);

  const handleClick = async (notif) => {
    // console.log("Before Click:", notif.notification_id, notif.is_read);

    const readNotif = { ...notif, is_read: 1 };
    setSelectedNotif(readNotif);

    // console.log("Clicked:", notif);
    if (!notif.is_read) {
      await markAsRead(notif.notification_id);
    }
  };

  const formatTime = (dt) => {
    if (!dt) return '';
    return new Date(dt).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      'Samanvay': 'bg-blue-500',
      'Collector': 'bg-purple-500',
      'LAO': 'bg-green-600',
      'SLR': 'bg-orange-500',
      'Surveyor': 'bg-yellow-600',
      'Payment': 'bg-emerald-600'
    };
    return colors[type] || 'bg-blue-600';
  };

  return (
    <div
      className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-gray-50 shadow-2xl z-50 flex flex-col"
      style={{ animation: 'slideInRight 0.25s ease-out' }}
    >
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      {/* ===== LIST VIEW ===== */}
      {!selectedNotif ? (
        <>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200 flex-shrink-0 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center">
                <Bell className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-900">Notifications</h2>
                <p className="text-xs text-gray-400">{notifications.length} total</p>
              </div>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${unreadCount === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    }`}
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
                  <Bell className="w-8 h-8 opacity-30" />
                </div>
                <p className="text-sm font-medium">No notifications yet</p>
                <p className="text-xs text-gray-300 mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notif) => (
                  <div
                    key={notif.notification_id}
                    onClick={() => handleClick(notif)}
                    className={`flex gap-3 px-4 py-3.5 cursor-pointer transition-all group ${!notif.is_read
                      ? 'bg-blue-50/70 border-l-4 border-l-blue-500 hover:bg-blue-100/60'
                      : 'bg-white border-l-4 border-l-transparent hover:bg-gray-50'
                      }`}
                  >
                    {/* Dot */}
                    <div className="flex-shrink-0 mt-2">
                      <div className={`w-2 h-2 rounded-full transition-all ${!notif.is_read ? 'bg-blue-500' : 'bg-gray-200'
                        }`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className={`${getTypeColor(notif.type)} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>
                          {notif.type}
                        </span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">
                          {formatTime(notif.cre_usr_dt)}
                        </span>
                      </div>
                      <p className={`text-sm mb-0.5 leading-snug truncate ${!notif.is_read
                        ? 'font-semibold text-gray-900'
                        : 'font-medium text-gray-500'
                        }`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-gray-400 line-clamp-1">{notif.message}</p>
                    </div>

                    {/* Arrow */}
                    <div className="self-center text-gray-300 group-hover:text-blue-400 transition-colors flex-shrink-0">
                      <ChevronDown className="w-4 h-4 -rotate-90" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        // ===== DETAIL VIEW =====
        <>
          <div className="flex items-center gap-3 px-5 py-4 bg-white border-b border-gray-200 flex-shrink-0 shadow-sm">
            <button
              onClick={() => setSelectedNotif(null)}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h2 className="text-sm font-bold text-gray-900 flex-1">Notification Detail</h2>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`${getTypeColor(selectedNotif.type)} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                {selectedNotif.type}
              </span>
              {/* ✅ is_read status badge — live update hoga */}
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${selectedNotif.is_read === 1
                ? 'bg-green-100 text-green-700'
                : 'bg-orange-100 text-orange-700'
                }`}>
                {selectedNotif.is_read === 1 ? '✓ Read' : '● Unread'}
              </span>
            </div>

            {/* Title */}
            <h3 className="text-base font-bold text-gray-900 leading-snug">
              {selectedNotif.title}
            </h3>

            {/* Message */}
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-2 font-semibold">Message</p>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                {selectedNotif.message}
              </p>
            </div>

            {/* Stage Info */}
            {/* <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-2xl p-3.5 border border-gray-100 shadow-sm">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5 font-semibold">
                  From Stage
                </p>
                <p className="text-sm font-bold text-gray-700">
                  {selectedNotif.from_stage_name}
                </p>
              </div>

              <div className="bg-blue-50 rounded-2xl p-3.5 border border-blue-100 shadow-sm">
                <p className="text-[10px] text-blue-400 uppercase tracking-wide mb-1.5 font-semibold">
                  To Stage
                </p>
                <p className="text-sm font-bold text-blue-700">
                  {selectedNotif.to_stage_name}
                </p>
              </div>
            </div> */}

            {/* Time */}
            <div className="bg-white rounded-2xl p-3.5 border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1.5 font-semibold">Received At</p>
              <p className="text-sm font-medium text-gray-700">{formatTime(selectedNotif.cre_usr_dt)}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================
// LanguageSelector
// ============================================
const LanguageSelector = ({ language, changeLanguage, translations }) => {
  const [showLanguage, setShowLanguage] = useState(false);
  const ref = useRef(null);
  const options = [
    { code: 'EN', name: 'English', flag: '🇺🇸' },
    // { code: 'HI', name: 'हिंदी', flag: '🇮🇳' },
    { code: 'MR', name: 'मराठी', flag: '🇮🇳' }
  ];
  const map = { 'EN': 'en', 'HI': 'hi', 'MR': 'mr' };
  const getCurrent = () => Object.entries(map).find(([, v]) => v === language)?.[0] || 'EN';

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowLanguage(false); };
    if (showLanguage) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showLanguage]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setShowLanguage(!showLanguage)}
        className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border-2 rounded-xl shadow-sm transition-all duration-200 ${showLanguage ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'
          }`}
      >
        <Globe className={`w-3 h-3 sm:w-4 sm:h-4 ${showLanguage ? 'text-blue-600' : 'text-gray-500'}`} />
        <span className={`font-semibold ${showLanguage ? 'text-blue-700' : 'text-gray-800'}`}>{getCurrent()}</span>
        <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showLanguage ? 'rotate-180 text-blue-600' : 'text-gray-400'}`} />
      </button>
      {showLanguage && (
        <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 z-[9999] py-2">
          <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider border-b mb-1">
            {translations[language].SelectLanguage}
          </div>
          {options.map(lang => (
            <button
              key={lang.code}
              onClick={() => { changeLanguage(map[lang.code]); setShowLanguage(false); }}
              className={`w-full text-left px-3 py-2 flex items-center space-x-3 text-sm transition-all ${getCurrent() === lang.code ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500' : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              <span>{lang.flag}</span>
              <span className="flex-1 font-medium">{lang.name}</span>
              {getCurrent() === lang.code && <Check className="w-3.5 h-3.5 text-blue-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// UserProfileDropdown
// ============================================
const UserProfileDropdown = ({ userInfo, onOpenProfile }) => {
  const [showProfile, setShowProfile] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const ref = useRef(null);
  const { language, translations } = useLanguage();

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try { await apiService.logout(); } catch { }
    finally { localStorage.clear(); sessionStorage.clear(); window.location.href = '/'; }
  };

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowProfile(false); };
    if (showProfile) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showProfile]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setShowProfile(!showProfile)}
        className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 rounded-xl transition-all duration-200 ${showProfile ? 'bg-blue-50 shadow-lg ring-2 ring-blue-200' : 'bg-white hover:bg-blue-50 hover:shadow-md'
          }`}
      >
        <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-xl flex items-center justify-center flex-shrink-0 ${showProfile ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-gradient-to-br from-blue-600 to-blue-800'
          }`}>
          <span className="text-xs sm:text-sm font-bold text-white">{userInfo.name.charAt(0)}</span>
        </div>
        <div className="hidden sm:block text-left min-w-0">
          <p className={`text-sm font-semibold truncate ${showProfile ? 'text-blue-700' : 'text-gray-900'}`}>{userInfo.name}</p>
          <p className={`text-xs truncate ${showProfile ? 'text-blue-600' : 'text-gray-500'}`}>{userInfo.role}</p>
        </div>
        <ChevronDown className={`w-2.5 h-2.5 sm:w-3 sm:h-3 transition-transform flex-shrink-0 ${showProfile ? 'rotate-180 text-blue-600' : 'text-gray-500'}`} />
      </button>

      {showProfile && (
        <div className="absolute right-0 mt-1 w-48 sm:w-56 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200/50 z-[9999] overflow-hidden">
          <div className="p-3 border-b bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
                <span className="text-base font-bold text-white">{userInfo.name.charAt(0)}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 truncate text-sm">{userInfo.name}</p>
                <p className="text-xs text-blue-600 truncate">{userInfo.email}</p>
                <p className="text-xs text-gray-500 truncate">{userInfo.role}</p>
              </div>
            </div>
          </div>
          <div className="py-2 px-2">
            <button
              onClick={() => { setShowProfile(false); onOpenProfile(); }}
              className="w-full px-3 py-2.5 text-left flex items-center space-x-3 text-sm text-gray-700 hover:bg-blue-50 rounded-xl transition-all group"
            >
              <User className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
              <span className="font-medium">{translations[language].Profile}</span>
            </button>
            <button className="w-full px-3 py-2.5 text-left flex items-center space-x-3 text-sm text-gray-700 hover:bg-blue-50 rounded-xl transition-all group">
              <Settings className="w-4 h-4 text-gray-500 group-hover:text-blue-600" />
              <span className="font-medium">{translations[language].Settings}</span>
            </button>
          </div>
          <div className="border-t p-2">
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full px-3 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-sm font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all disabled:opacity-50"
            >
              <LogOut className={`w-4 h-4 ${isLoggingOut ? 'animate-spin' : ''}`} />
              <span>{isLoggingOut ? translations[language].SigningOut : translations[language].SignOut}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ProposalInfo
// ============================================
const ProposalInfo = ({ currentProposal }) => {
  const { language, translations } = useLanguage();
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = userData?.data?.user_role || userData?.user_role;
  // const overrideProposalNo = localStorage.getItem("selectedProposalNo");
  // const proposalNo = overrideProposalNo || currentProposal?.acquisition_proposal_no || '—';
  const proposalNo = currentProposal?.acquisition_proposal_no || '—';
  const bhusaNo = currentProposal?.section_11_bhusampadan_no || null;
  return (
    <div className="hidden md:flex items-center space-x-4">
      <div className="w-1 h-8 bg-blue-700 rounded-full" />
      <div>
        <h1 className="text-sm lg:text-base font-semibold text-gray-900">
          {bhusaNo ? translations[language].Bhusampadan : translations[language].Proposal}
        </h1>
        <p className="text-xs lg:text-sm font-medium text-blue-700">
          {/* {roleId === 18 ? proposalNo : bhusaNo || proposalNo} */}
          {bhusaNo ? bhusaNo : proposalNo}
        </p>
      </div>
    </div>
  );
};

// ============================================
// ExtendDateInfo
// ============================================
const ExtendDateInfo = ({ extendData, loading, currentProposal }) => {
  const fmt = (d) => { try { return new Date(d).toLocaleDateString("en-GB"); } catch { return '—'; } };

  const gazette = currentProposal?.section11_published_gazette;

  const proposalDate = gazette
    ? (() => {
      let date = new Date(gazette);
      date.setFullYear(date.getFullYear() + 1);
      date.setDate(date.getDate() - 1);
      return date;
    })()
    : null;

  const latest = extendData && Array.isArray(extendData) && extendData.length > 0
    ? [...extendData].sort((a, b) => b.extend_id - a.extend_id)[0]
    : null;

  const displayDate = latest?.extended_date
    ? fmt(latest.extended_date)
    : proposalDate
      ? fmt(proposalDate)
      : null;

  return (
    <div className="relative flex-1 overflow-hidden mx-4">
      <style>{`
        @keyframes slideTextAcross { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .slide-text { display:inline-block; animation:slideTextAcross 15s linear infinite; white-space:nowrap; padding-left:100%; }
        .slide-container { position:relative; width:100%; height:2rem; overflow:hidden; display:flex; align-items:center; }
      `}</style>

      {loading
        ? <div className="text-gray-400 text-sm animate-pulse">Loading...</div>
        : displayDate
          ? <div className="slide-container">
            <div className="text-red-600 text-sm font-bold slide-text">
              {latest?.extended_date ? "मुदत वाढ दिनांक:" : "व्यपगत दिनांक:"} {displayDate}
            </div>
          </div>
          : null}
    </div>
  );
};

// ============================================
// MAIN NAVBAR
// ============================================
const Navbar = ({ pageTitle, subTitle, toggleSidebar, hideProposalInfo }) => {
  const { language, changeLanguage, translations } = useLanguage();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const userInfo = useUserProfile();
  const currentProject = useCurrentProject();
  const currentProposal = useCurrentProposal();
  const { extendData, loading } = useExtendDateData();

  return (
    <>
      <nav className="bg-white border-b border-gray-200">
        <div className="px-3 sm:px-6 py-2 sm:py-3">
          <div className="flex items-center justify-between space-x-2 sm:space-x-4">
            {!hideProposalInfo && <ProposalInfo currentProposal={currentProposal} />}
            {!hideProposalInfo && <ExtendDateInfo extendData={extendData} loading={loading} currentProposal={currentProposal} />}

            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0 ml-auto">
              <LanguageSelector language={language} changeLanguage={changeLanguage} translations={translations} />

              {/* Bell Button */}
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Panel */}
              {notifOpen && (
                <>
                  <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setNotifOpen(false)} />
                  <NotificationPanel
                    notifications={notifications}
                    unreadCount={unreadCount}
                    markAsRead={markAsRead}
                    markAllAsRead={markAllAsRead}
                    onClose={() => setNotifOpen(false)}
                  />
                </>
              )}

              <div className="w-px h-4 sm:h-5 bg-gray-300" />
              <UserProfileDropdown userInfo={userInfo} onOpenProfile={() => setShowProfileModal(true)} />
            </div>
          </div>
        </div>
      </nav>
      {showProfileModal && <UserProfileModal onClose={() => setShowProfileModal(false)} />}    </>
  );
};

export default Navbar;